import React, { useState } from 'react';
import { X, AlertTriangle, Loader } from 'lucide-react';
import { toast } from 'react-toastify';
import { useLazyGetPatientDeletePreviewQuery, useDeletePatientMutation } from '../../api/patientsApi';

const fmtINR = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v ?? 0);

/**
 * Preview-then-confirm patient delete. Fetches everything the delete will cascade through
 * (appointments, sessions, invoices/receipts, session packages, treatment plans, consultations —
 * see backend PatientService.DeleteAsync) and requires the operator to see those counts before
 * the irreversible action is available.
 */
export default function DeletePatientModal({ patientId, patientName, patientCode, onClose, onDeleted }) {
  const [fetchPreview, { data: preview, isFetching, isError }] = useLazyGetPatientDeletePreviewQuery();
  const [deletePatient, { isLoading: deleting }] = useDeletePatientMutation();
  const [ready, setReady] = useState(false);

  React.useEffect(() => {
    fetchPreview(patientId).unwrap().catch(() => {});
  }, [patientId, fetchPreview]);

  const confirmDelete = async () => {
    try {
      await deletePatient(patientId).unwrap();
      toast.success(`${patientName || 'Patient'} and all associated data deleted`);
      onDeleted?.();
    } catch (e) {
      toast.error(e?.data?.error?.message || 'Could not delete this patient.');
    }
  };

  const rows = preview ? [
    { label: 'Appointments', value: preview.appointmentCount },
    { label: 'Sessions', value: preview.sessionCount },
    { label: 'Invoices', value: preview.invoiceCount, sub: preview.invoiceCount > 0 ? `${fmtINR(preview.totalBilled)} billed, ${fmtINR(preview.totalPaid)} paid` : null },
    { label: 'Receipts', value: preview.receiptCount },
    { label: 'Session packages', value: preview.sessionPackageCount },
    { label: 'Treatment plans', value: preview.treatmentPlanCount },
    { label: 'Consultations', value: preview.consultationCount },
  ] : [];
  const hasAnything = rows.some((r) => r.value > 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ color: 'var(--red-700)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={18} /> Delete Patient
          </h2>
          <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '0.85rem', marginTop: 0 }}>
            Deleting <strong>{patientName || 'this patient'}</strong> {patientCode ? `(${patientCode})` : ''} is <strong>irreversible</strong>.
            Everything below will be permanently deleted along with the patient record:
          </p>

          {isFetching && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 0', color: 'var(--text-secondary)' }}>
              <Loader size={14} className="spin" /> Checking what's attached…
            </div>
          )}

          {isError && (
            <p style={{ color: 'var(--red-700)', fontSize: '0.85rem' }}>Could not load a preview — try closing and reopening this dialog.</p>
          )}

          {preview && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginTop: 4 }}>
              {rows.map((r) => (
                <div key={r.label} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                  padding: '8px 12px', borderTop: '1px solid var(--border)',
                  background: r.value > 0 ? 'var(--red-50, #fef2f2)' : 'transparent',
                }}>
                  <span style={{ fontSize: '0.82rem' }}>{r.label}</span>
                  <span style={{ textAlign: 'right' }}>
                    <strong style={{ fontSize: '0.9rem', color: r.value > 0 ? 'var(--red-700)' : 'var(--text-muted)' }}>{r.value}</strong>
                    {r.sub && <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{r.sub}</div>}
                  </span>
                </div>
              ))}
              {!hasAnything && (
                <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border)', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  Nothing attached — this patient record is empty.
                </div>
              )}
            </div>
          )}

          {preview && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: '0.82rem', cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={ready} onChange={(e) => setReady(e.target.checked)} />
              I understand this permanently deletes the patient and everything listed above.
            </label>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={!preview || !ready || deleting}
            onClick={confirmDelete}
          >
            {deleting ? 'Deleting…' : 'Delete Patient Permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}
